import { createTheme } from '@abyss/web/tools/theme';
import { LoadingSpinner } from '@abyss/web/ui/LoadingSpinner';
import { OverlayProvider } from '@abyss/web/ui/OverlayProvider';
import { PrintProvider } from '@abyss/web/ui/PrintProvider';
import { RouterProvider } from '@abyss/web/ui/RouterProvider';
import { ThemeProvider } from '@abyss/web/ui/ThemeProvider';
import { useEffect, useState } from 'react';

import { LDProvider } from '../../common/LDProvider/LDProvider';
import { CountySearchContextProvider } from '../../context/CountySearchContext';
import { SearchFilterContextProvider } from '../../context/SearchFilterContext';
import { useCurrentMember } from '../../hooks/useCurrentMember/useCurrentMember';
import { registerEvents } from '../../tools/events';
import { ConditionalWrapper } from '../../utils/portal.utils';
import { callGuestSessionUrl } from '../../utils/session.utils';
import { Analytics } from '../ProviderSearch/context/Analytics';
import { Configuration } from '../ProviderSearch/context/Configuration';
import { Internationalization } from '../ProviderSearch/context/Internationalization';
import { SearchLocation } from '../ProviderSearch/context/SearchLocation';
import { createRouter } from '../ProviderSearchForPortal/createRouter';
import { uhcThemeOverride } from '../themeOverrides/uhcThemeOverride';
import { GuestIFPTimeoutModal, GuestTimeoutModalWrapper } from './modals';
import { GuestRoutes } from './GuestRoutes';

export const GuestApp = () => {
  // Create UHC theme with custom overrides for guest experience
  // @ts-expect-error - createTheme type definition doesn't match the actual implementation
  const theme = createTheme('uhc', uhcThemeOverride);
  const { isIFPEarlyPay = false } = useCurrentMember() || {};
  return (
    <Internationalization>
      <Analytics>
        <PrintProvider>
          <OverlayProvider>
            <ThemeProvider theme={theme}>
              {isIFPEarlyPay ? <GuestIFPTimeoutModal /> : <GuestTimeoutModalWrapper />}
              <SearchFilterContextProvider>
                <CountySearchContextProvider>
                  <ConditionalWrapper
                    isCondition={isIFPEarlyPay}
                    wrapper={(children) => (
                      <SearchLocation>{children}</SearchLocation>
                    )}
                  >
                    <GuestRoutes />
                  </ConditionalWrapper>
                </CountySearchContextProvider>
              </SearchFilterContextProvider>
            </ThemeProvider>
          </OverlayProvider>
        </PrintProvider>
      </Analytics>
    </Internationalization>
  );
};

export const GuestExperience = (args) => {
  const basePath = args?.basePath || '/guest';
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const router = createRouter(GuestApp, basePath);

  useEffect(() => {
    registerEvents(undefined, true);

    const useGuestPersistence = basePath === '/guest-plan-selection';
    callGuestSessionUrl(useGuestPersistence).then(() => {
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return (
      <LoadingSpinner
        ariaLoadingLabel="loading results"
        data-testid="loading-spinner"
        isLoading={isLoading}
        size="$lg"
      />
    );
  }

  return (
    <Configuration>
      <LDProvider>
        <RouterProvider router={router} />
      </LDProvider>
    </Configuration>
  );
};
